import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useFamily } from "@/hooks/useFamily";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useParentDashboardStatus } from "@/hooks/useParentDashboardStatus";
import { useCustodyForDate } from "@/hooks/useTodayCustody";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { DashboardChildCard } from "@/components/DashboardChildCard";
import { CustodySummary } from "@/components/CustodySummary";
import { EmbeddedCheckoutDialog } from "@/components/EmbeddedCheckoutDialog";
import { PLAN_CONFIG } from "@/lib/plans";
import { getDateString, formatDate } from "@/lib/family-date";
import { Sparkles } from "lucide-react";

export default function Index() {
  const { user } = useAuth();
  const { membership, memberships, timezone } = useFamily();
  const { children, hasAnySplitChild } = useAllChildren();
  const firstFamilyId = memberships[0]?.family_id ?? null;
  const { hasPlus, isLoading: subLoading } = useEntitlements(firstFamilyId);
  const { data: statusMap } = useParentDashboardStatus(children, timezone);
  const { isWithOtherParent } = useCustodyForDate();
  const navigate = useNavigate();
  const [trialCheckoutOpen, setTrialCheckoutOpen] = useState(false);
  

  const displayName = membership?.display_name || user?.email;
  const firstName = displayName?.includes("@") ? displayName.split("@")[0] : displayName || "der";

  const familyIds = memberships.map((m) => m.family_id);
  const todayStr = getDateString(new Date(), timezone);

  const { data: nextHandover } = useQuery({
    queryKey: ["parent", "next-handover", familyIds.join(","), todayStr],
    queryFn: async () => {
      if (familyIds.length === 0) return null;
      const { data, error } = await supabase
        .from("custody_days")
        .select("*")
        .in("family_id", familyIds)
        .eq("is_handover", true)
        .gte("date", todayStr)
        .order("date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: familyIds.length > 0 && hasAnySplitChild,
  });

  const handoverChild = nextHandover ? children.find((c) => c.id === nextHandover.child_id) : null;

  return (
    <div className="min-h-full bg-background px-4 pt-safe pb-6">
      <div className="mx-auto max-w-sm space-y-7">
        <h1 className="text-center font-heading text-2xl font-bold tracking-tight">Hei, {firstName} 👋</h1>

        {/* Trial CTA for free users */}
        {!hasPlus && !subLoading && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="shrink-0 rounded-lg bg-primary/10 p-1.5">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">Prøv Flyt Pluss 14 dager gratis</p>
                  <p className="text-xs text-muted-foreground">Full historikk, rapporter og mer innsikt</p>
                </div>
              </div>
              <Button size="sm" className="shrink-0" onClick={() => setTrialCheckoutOpen(true)}>
                Prøv gratis
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Next handover */}
        {nextHandover && handoverChild && (
          <Card className="border-primary/20">
            <CardContent className="p-4 space-y-1">
              <p className="text-xs text-muted-foreground font-medium">🔄 Neste byttedag</p>
              <p className="text-sm font-medium capitalize">
                {formatDate(nextHandover.date + "T12:00:00", timezone, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {handoverChild.avatar_emoji} {handoverChild.name}
              </p>
              <Button variant="outline" size="sm" className="mt-2 rounded-xl" onClick={() => navigate("/custody")}>
                Gå til Samarbeid
              </Button>
            </CardContent>
          </Card>
        )}

        {hasAnySplitChild && <CustodySummary children={children} />}

        {/* Children cards */}
        <div className="space-y-3">
          {/*<h2 className="text-lg font-display font-semibold">Barna</h2> */}
          {children.map((child) => {
            const status = statusMap?.get(child.id);
            return (
              <DashboardChildCard
                key={child.id}
                child={child}
                taskDone={status?.taskDone ?? 0}
                taskTotal={status?.taskTotal ?? 0}
                homeworkCount={status?.homeworkCount ?? 0}
                reminderCount={status?.reminderCount ?? 0}
                nextHandover={hasAnySplitChild ? nextHandover : null}
                timezone={timezone}
                isWithOtherParent={child.custody_enabled ? isWithOtherParent(child.id) : false}
                alerts={status?.alerts ?? []}
              />
            );
          })}
        </div>
      </div>
      {!hasPlus && (
        <EmbeddedCheckoutDialog
          open={trialCheckoutOpen}
          onOpenChange={setTrialCheckoutOpen}
          priceId={PLAN_CONFIG.plus.price_id}
          familyId={firstFamilyId}
          planLabel="Flyt Pluss"
        />
      )}
    </div>
  );
}
